import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { trackTrainingEvent } from '../trainingAnalytics';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../trainingWhatsApp';

const labels = {
  fr: {
    title: 'Choisissez votre mode de formation',
    introduction: 'Sélectionnez le format qui vous convient pour continuer sur WhatsApp.',
    close: 'Fermer',
    inPerson: 'Présentiel',
    online: 'En ligne',
    fallback: 'WhatsApp ne s’est pas ouvert. Copiez ce message et contactez-nous au',
    copy: 'Copier le message',
    copied: 'Message copié',
  },
  en: {
    title: 'Choose your training mode',
    introduction: 'Select the format that suits you to continue on WhatsApp.',
    close: 'Close',
    inPerson: 'In person',
    online: 'Online',
    fallback: 'WhatsApp did not open. Copy this message and contact us at',
    copy: 'Copy message',
    copied: 'Message copied',
  },
};

export default function ModeSelectorDialog({ open, locale, offer, source, onClose }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const wasOpenRef = useRef(false);
  const controlledCloseRef = useRef(false);
  const [fallbackMessage, setFallbackMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const copy = labels[locale];
  const supportsNativeDialog = typeof HTMLDialogElement !== 'undefined'
    && typeof HTMLDialogElement.prototype.showModal === 'function';

  useLayoutEffect(() => {
    if (!open) {
      if (wasOpenRef.current) {
        const trigger = returnFocusRef.current;
        if (trigger && typeof trigger.focus === 'function') trigger.focus();
      }
      wasOpenRef.current = false;
      return undefined;
    }

    if (!wasOpenRef.current) {
      returnFocusRef.current = document.activeElement;
      wasOpenRef.current = true;
      controlledCloseRef.current = false;
    }

    const dialog = dialogRef.current;
    if (supportsNativeDialog && dialog && !dialog.open) {
      dialog.showModal();
    }
    dialog?.focus();

    return undefined;
  }, [open, supportsNativeDialog]);

  if (!open || !copy || !offer) return null;

  const restoreFocus = () => {
    const trigger = returnFocusRef.current;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
  };

  const closeDialog = () => {
    const dialog = dialogRef.current;
    if (supportsNativeDialog && dialog?.open && typeof dialog.close === 'function') {
      controlledCloseRef.current = true;
      dialog.close();
    }
    setFallbackMessage('');
    setCopied(false);
    onClose();
    restoreFocus();
  };

  const handleCancel = (event) => {
    event.preventDefault();
    closeDialog();
  };

  const handleNativeClose = () => {
    if (controlledCloseRef.current) {
      controlledCloseRef.current = false;
      return;
    }
    onClose();
    restoreFocus();
  };

  const handleFallbackKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeDialog();
  };

  const handleFallbackBackdropClick = (event) => {
    if (event.target === event.currentTarget) closeDialog();
  };

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const inside = event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom;
    if (!inside) closeDialog();
  };

  const handleMode = (mode) => {
    const dimensions = { offerKey: offer.key, locale, mode };
    try {
      trackTrainingEvent('mode_select', dimensions);
    } catch {
      // Conversion must continue if an analytics adapter is unavailable.
    }

    const message = buildWhatsAppMessage({ locale, offer, mode });
    const url = buildWhatsAppUrl({ locale, offer, mode });

    try {
      trackTrainingEvent('whatsapp_cta_click', { ...dimensions, source });
    } catch {
      // Conversion must continue if an analytics adapter is unavailable.
    }

    let popup = null;
    try {
      popup = window.open('', '_blank');
    } catch {
      setFallbackMessage(message);
      setCopied(false);
      return;
    }

    if (!popup) {
      setFallbackMessage(message);
      setCopied(false);
      return;
    }

    try {
      popup.opener = null;
      const navigation = popup.document.createElement('a');
      navigation.href = url;
      navigation.target = '_self';
      navigation.rel = 'noreferrer noopener';
      popup.document.body.appendChild(navigation);
      navigation.click();
    } catch {
      try {
        popup.close();
      } catch {
        // Recovery remains local even if the transient context cannot be closed.
      }
      setFallbackMessage(message);
      setCopied(false);
      return;
    }

    closeDialog();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fallbackMessage);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const content = (
    <div className="training-dialog__panel">
      <h2 id={titleId}>{copy.title}</h2>
      <p>{copy.introduction}</p>
      <div className="training-dialog__modes">
        <button type="button" onClick={() => handleMode('inPerson')}>{copy.inPerson}</button>
        <button type="button" onClick={() => handleMode('online')}>{copy.online}</button>
      </div>
      {fallbackMessage ? (
        <div className="training-dialog__fallback">
          <p>{copy.fallback} <strong>+237 653 45 96 38</strong></p>
          <p className="training-dialog__message">{fallbackMessage}</p>
          <button type="button" onClick={handleCopy}>{copied ? copy.copied : copy.copy}</button>
        </div>
      ) : null}
      <button className="training-dialog__close" type="button" onClick={closeDialog}>
        {copy.close}
      </button>
    </div>
  );

  if (!supportsNativeDialog) {
    return (
      <div
        ref={dialogRef}
        className="training-dialog training-dialog--fallback training-dialog-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleFallbackKeyDown}
        onClick={handleFallbackBackdropClick}
      >
        {content}
      </div>
    );
  }

  return (
    <dialog
      ref={dialogRef}
      className="training-dialog"
      aria-labelledby={titleId}
      closedby="any"
      tabIndex={-1}
      onCancel={handleCancel}
      onClose={handleNativeClose}
      onClick={handleBackdropClick}
    >
      {content}
    </dialog>
  );
}
